import { useState, useEffect, useCallback } from "react";
import { STORAGE_KEYS } from "../util/constants";

/**
 * Hook para gerenciar localStorage de forma segura
 * @param {string} key - Chave do localStorage
 * @param {any} initialValue - Valor inicial se não existir no storage
 * @returns {[any, function, function]} - [valor, setValue, removeValue]
 */
export const useLocalStorage = (key, initialValue = null) => {
  // Estado para armazenar o valor
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Erro ao ler localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Função para atualizar o valor
  const setValue = useCallback(
    (value) => {
      try {
        // Permite que value seja uma função para ter a mesma API do useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.error(`Erro ao salvar no localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  // Função para remover o valor
  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Erro ao remover localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  // Sincroniza com mudanças em outras abas/janelas
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          console.error(`Erro ao sincronizar localStorage key "${key}":`, error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key]);

  return [storedValue, setValue, removeValue];
};

/**
 * Hook específico para userData (compatibilidade)
 */
export const useUserData = () => {
  return useLocalStorage(STORAGE_KEYS.USER_DATA, null);
};

